import { debounce } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { UseFormRegister } from "react-hook-form/dist/types/form";
import { CircleSpinner } from "react-spinners-kit";
import styled, { css, useTheme } from "styled-components";

import { ResidenceService } from "@/api/services/residence.service";
import HelperText from "@/components/form/HelperText";
import IconWrapper from "@/components/icons/IconWrapper/IconWrapper";
import { COLORS } from "@/styles/theme";
import { ColorType } from "@/types/colorTypes";
import { InputStateExtendedType, InputType } from "@/types/formTypes";
import useIsMobileOS from "@/utils/hooks/useIsMobileOS";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Label of the input field. */
  label?: string;

  /** React or custom Icon to be placed in front of the input  */
  iconLeft?: ReactNode;

  /** React or custom Icon to be placed in after of the input  */
  iconRight?: ReactNode;

  /** Small text that will appear under the input field. */
  helperText?: string;

  /** Type of input. Same as HTML Input type. */
  type?: InputType;

  /** Placeholder for the input field */
  placeholder?: string;

  /** Wether the  input field should be disabled */
  disabled?: boolean;

  /** Wether the  input field is active */
  active?: boolean;

  /** Wether the  input field is required */
  required?: boolean;
  requiredSignColor?: string;

  /** Wether the  input field has errors */
  hasError?: boolean;

  /** Name of the input field. required for submitting the form */
  name?: string;

  defaultValue?: string;

  /** Callback to handle the input */
  onChange?: (_x: any) => void;

  labelClass?: string;

  /** React hook form register function for error handling */
  register?: UseFormRegister<any>;

  /** The color of the border */
  borderColor?: ColorType;

  /** Text in parenthesis */
  extraDesc?: string;

  /** If the input is for a residence search */
  isResidenceInput?: boolean;
}

const InputWrapper = styled.div<InputStateExtendedType>`
  display: flex;
  flex-direction: column;
  cursor: text;
  justify-content: space-between;
  //height: ${({ hasError }) => (hasError ? "100%" : "auto")};
  height: 100%;

  label {
    margin-bottom: 16px;
    font-weight: 700;
    font-size: 18px;
    line-height: 160%;

    color: var(--color-white);

    span.required-sign {
      color: var(--color-orange);
    }
  }

  .input-field {
    border: 1px solid;
    background-color: white;
    width: 100%;
    border-radius: 8px;
    display: flex;
    align-items: center;
    padding: 10px 12px 10px 16px;

    ${({ isMobileOS, hasValue }) =>
      isMobileOS &&
      css`
        input[type="date"]::before {
          content: ${hasValue ? "none" : "attr(placeholder)"};
          color: var(--color-input-placeholder) !important;
          font-weight: 300 !important;
          font-family: "Avenir" !important;
          opacity: 1;
        }
      `}
    input {
      border: none;
      width: 100%;
      margin-top: 3px;

      font-weight: 400;
      font-size: 18px;
      line-height: 100%;
      font-family: "Avenir" !important;
      color: var(--color-input);
      background-color: transparent;
      min-height: 1.5rem;

      ::placeholder {
        color: var(--color-input-placeholder);
        font-weight: 300 !important;
        font-family: "Avenir" !important;
        opacity: 1;
      }

      &:focus {
        outline: 0;
      }

      &:placeholder-shown {
        text-overflow: ellipsis;
      }
    }
  }

  footer {
    display: flex;
    align-items: center;
    margin-top: 13.5px;
  }

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: not-allowed;

          .input-field {
            opacity: 0.3;

            input {
              cursor: not-allowed;
            }
          }

          footer {
            opacity: 0.3;
          }
        `
      : null}

  ${({ active }) =>
    active
      ? css`
          .input-field {
            border-color: black;
          }
        `
      : null}

    ${({ hasError }) =>
    hasError
      ? css`
          \ .input-field {
            background-color: var(--color-red-opacity-10);
            border-color: var(--color-red);
          }

          footer {
            svg path {
              fill: var(--color-red);
            }

            p {
              color: var(--color-red);
            }
          }
        `
      : null}
`;

export default function Input({
  iconLeft,
  iconRight,
  type = "text",
  label,
  helperText,
  placeholder = "Vul hier je reactie in",
  active = false,
  disabled = false,
  hasError = false,
  name,
  onChange,
  register,
  required,
  requiredSignColor = COLORS.tertiary.normal,
  labelClass,
  borderColor = "white",
  extraDesc = "",
  className,
  isResidenceInput,
  ...rest
}: InputProps) {
  const { colors } = useTheme();
  const isMobileOS = useIsMobileOS();
  const [residenceSearch, setResidenceSearch] = useState<string>("");
  const [residenceData, setResidenceData] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [suggestionsRef]);

  const formRegister =
    register && name
      ? register(name, {
          required: required ? "Dit veld is verplicht" : false,
          valueAsNumber: type === "number",
        })
      : {};

  const residenceMutation = useMutation({
    mutationFn: async (searchQuery: string) => {
      return await ResidenceService.getResidence(
        { search: searchQuery },
        new AbortController().signal,
      );
    },
    onSuccess: (data) => {
      const places = Array.from(
        new Set(
          data.features.map((feature) => {
            return feature.properties.context.place.name;
          }),
        ),
      );

      // Set residenceData to all unique "place" entries
      setResidenceData(places);

      // If residenceSearch is empty, clear residenceData
      if (residenceSearch === "") {
        setResidenceData([]);
      }
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMutate = useCallback(
    debounce((searchQuery: string) => {
      residenceMutation.mutate(searchQuery);
    }, 300),
    [],
  );

  useEffect(() => {
    if (isResidenceInput && residenceSearch !== "") {
      debouncedMutate(residenceSearch);
    }
  }, [residenceSearch, isResidenceInput, debouncedMutate]);

  return (
    <InputWrapper
      disabled={disabled}
      active={active}
      hasError={hasError}
      className={className}
      isMobileOS={isMobileOS}
      hasValue={!!rest.value}
    >
      {!!label && (
        <label className={labelClass}>
          {label}{" "}
          {required && (
            <span
              className={`required-sign`}
              style={{ color: requiredSignColor }}
            >
              *
            </span>
          )}{" "}
          {extraDesc && <span className={"extra-desc"}>({extraDesc})</span>}
        </label>
      )}
      <div>
        <div
          style={{ borderColor: colors[borderColor].normal }}
          className={`input-field relative ${hasError ? "error-input" : ""} ${rest.hidden ? "hidden" : ""}`}
        >
          {!!iconLeft && (
            <IconWrapper style={{ marginRight: 10 }}>{iconLeft}</IconWrapper>
          )}
          <input
            type={type}
            placeholder={placeholder}
            {...formRegister}
            {...rest}
            value={isResidenceInput ? residenceSearch : rest.value}
            onChange={(e) => {
              onChange?.(e);
              if (isResidenceInput) {
                setResidenceSearch(e.currentTarget.value);
                setShowSuggestions(true);
              }
            }}
          />
          {!!iconRight && !residenceMutation.isPending && (
            <IconWrapper style={{ marginLeft: 10 }}>{iconRight}</IconWrapper>
          )}
          {residenceMutation.isPending && (
            <CircleSpinner size={20} color={COLORS.info.normal} />
          )}
          {isResidenceInput && showSuggestions && residenceData.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute top-full left-0 z-10 mt-1 max-h-[200px] w-full overflow-y-auto rounded-lg bg-white"
            >
              {residenceData.map((residence, index) => (
                <div
                  key={index}
                  className="cursor-pointer p-2"
                  onClick={() => {
                    setResidenceSearch(residence);
                    onChange?.({ target: { value: residence } });
                    setResidenceData([]);
                    setShowSuggestions(false);
                  }}
                >
                  {residence}
                </div>
              ))}
            </div>
          )}
        </div>
        <HelperText text={helperText} />
      </div>
    </InputWrapper>
  );
}
